import React from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ThinkfabLogo } from '../components/Logo';
import mediaQueries from '../styles/mediaQueries';

const BPIFRANCE_ANNOUNCEMENT_URL =
  'https://presse.bpifrance.fr/france-2030-a-vivatech-stephanie-rist-annonce-les-nouveaux-laureats-de-linnovation-en-sante/?lang=fra';

interface IPartner {
  name: string;
  role?: string;
  logo?: string;
  isThinkfab?: boolean;
}

// Un partenaire sans logo s'affiche par son nom, en attendant le fichier officiel.
const partners: IPartner[] = [
  { name: 'Enovacom', role: 'Porteur du projet et chef de file industriel', logo: '/images/innopath/logo-enovacom.svg' },
  { name: 'Université de Reims Champagne-Ardenne' },
  { name: 'CHU de Reims', logo: '/images/innopath/logo-chu-reims.png' },
  { name: "Institut d'Intelligence Artificielle en Santé" },
  { name: 'AFAQAP', logo: '/images/innopath/logo-afaqap.png' },
  { name: 'Thinkfab', isThinkfab: true },
  { name: 'Zenidoc' },
];

// Fichiers officiels à déposer : kit France 2030 (gouvernement.fr) et espace Logos de Bpifrance Presse.
// Un logo absent est masqué plutôt qu'affiché cassé.
const fundingLogos = [
  { src: '/images/innopath/logo-france-2030.png', alt: 'France 2030' },
  { src: '/images/innopath/logo-bpifrance.png', alt: 'Bpifrance' },
];

const impulsionPillars = [
  {
    verb: 'Structurer',
    name: 'REPO',
    text: "Le référentiel national des modèles de comptes rendus, conçus par plus de 150 pathologistes et validés au niveau national. Il couvre la pathologie cancéreuse et non cancéreuse, pour tous les types de prélèvements.",
  },
  {
    verb: 'Produire',
    name: 'PULSE',
    text: "La solution de saisie des comptes rendus structurés au quotidien, nativement reliée au référentiel et interfaçable avec les systèmes de gestion de laboratoire.",
  },
  {
    verb: 'Exploiter',
    name: 'CUBE',
    text: "Le projet d'espace national de valorisation des données structurées, sous la gouvernance de la spécialité, au service de la qualité des pratiques et de la recherche.",
  },
];

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  font-family: var(--font-primary);
  padding: 0 20px 0px;
`;

const MainSection = styled.main`
  max-width: 1000px;
  margin: 0 auto;
`;

const HeroSection = styled.section`
  padding: 60px 100px;

  ${mediaQueries.large} {
    padding: 50px 100px;
  }

  ${mediaQueries.medium} {
    padding: 40px 40px;
  }

  ${mediaQueries.small} {
    padding: 30px 20px;
  }
`;

const TitleContainer = styled.div`
  margin-bottom: 40px;
  text-align: right;
  line-height: 1.1;
`;

const PageTitle = styled.h1`
  font-size: 40px;
  font-weight: 800;
  margin-bottom: 0px;
`;

const PageSubtitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 30px;
`;

const HeroLogosRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin: 40px 0 40px;
`;

const HeroFundingLogos = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const HeroFundingLogo = styled.img`
  max-height: 55px;
  object-fit: contain;

  ${mediaQueries.small} {
    max-height: 32px;
  }
`;

const HeroImpulsionLogo = styled.img`
  max-height: 55px;
  object-fit: contain;

  ${mediaQueries.small} {
    max-height: 32px;
  }
`;

const HighlightedText = styled.span`
  color: ${({theme}) => theme.colors.secondary};
  font-weight: 700;
`;

const Description = styled.p`
  font-size: 18px;
  line-height: 1.6;
  max-width: 800px;
  margin-bottom: 20px;
`;

const ContentSection = styled.section<{ tinted?: boolean }>`
  padding: 60px 40px;
  background-color: ${({ tinted }) => (tinted ? '#FEF2F1' : 'transparent')};
  border-radius: 32px;
  margin: 0 auto 60px;

  ${mediaQueries.large} {
    padding: 40px 30px;
  }

  ${mediaQueries.medium} {
    padding: 30px 20px;
  }

  ${mediaQueries.small} {
    padding: 20px 15px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  margin-bottom: 30px;
  color: ${({theme}) => theme.colors.secondary};

  ${mediaQueries.small} {
    font-size: 28px;
  }
`;

const ImpulsionIntro = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const PillarsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;

  ${mediaQueries.medium} {
    grid-template-columns: 1fr;
  }
`;

const Pillar = styled.div`
  border-top: 4px solid ${({theme}) => theme.colors.secondary};
  padding-top: 16px;
`;

const PillarVerb = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({theme}) => theme.colors.secondary};
  margin-bottom: 6px;
`;

const PillarName = styled.h4`
  font-size: 18px;
  margin-bottom: 10px;
  color: ${({theme}) => theme.colors.primary};
`;

const PillarText = styled.p`
  font-size: 15px;
  line-height: 1.5;
`;

const ExternalLink = styled.a`
  color: ${({theme}) => theme.colors.secondary};
  font-weight: 500;
  display: inline-flex;
  align-items: center;

  &:hover {
    text-decoration: underline;
  }

  &::after {
    content: "→";
    margin-left: 5px;
    transition: transform 0.2s;
  }

  &:hover::after {
    transform: translateX(3px);
  }
`;

const PartnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const PartnerCard = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 24px 20px;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
`;

const PartnerLogo = styled.img`
  max-height: 60px;
  max-width: 160px;
  object-fit: contain;
`;

const PartnerName = styled.span`
  font-size: 16px;
  font-weight: var(--font-weight-semibold);
  color: ${({theme}) => theme.colors.primary};
`;

const PartnerRole = styled.span`
  font-size: 13px;
  color: #777;
  margin-top: 10px;
`;

const ThinkfabPartnerLogo = styled(ThinkfabLogo)`
  font-size: 28px;
`;

const FundingNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 24px 30px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  margin-bottom: 60px;

  ${mediaQueries.small} {
    flex-direction: column;
    text-align: center;
  }
`;

const FundingLogos = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
`;

const FundingLogo = styled.img`
  max-height: 80px;
  object-fit: contain;
`;

const FundingText = styled.p`
  font-size: 16px;
  font-style: italic;
`;

const Innopath: React.FC = () => {
  return (
    <PageContainer>
      <SEO
        title="InnoPath-Fr"
        description="Thinkfab contribue à InnoPath-Fr, plateforme nationale souveraine en anatomopathologie, lauréate de France 2030 : structuration des comptes rendus et mutualisation des données."
        canonical={`${window.location.origin}/innopath`}
      />
      <Header backgroundColor="transparent" textColor="#011D3A" />

      <MainSection>
        <HeroSection>
          <TitleContainer>
            <PageTitle>
              <HighlightedText>InnoPath-Fr.</HighlightedText>
            </PageTitle>
            <PageSubtitle>La plateforme nationale de pathologie numérique.</PageSubtitle>
            <HeroLogosRow>
              <HeroFundingLogos>
                {fundingLogos.map((logo) => (
                  <HeroFundingLogo
                    key={logo.src}
                    src={logo.src}
                    alt={logo.alt}
                    onError={(event) => { event.currentTarget.style.display = 'none'; }}
                  />
                ))}
              </HeroFundingLogos>
              <HeroImpulsionLogo src="/images/projets/logo-impulsion.png" alt="Logo Impulsion" />
            </HeroLogosRow>
          </TitleContainer>

          <Description>
            InnoPath-Fr est un projet national lauréat de France 2030, au titre de l'appel à projets
            « Innovation en imagerie médicale ». Il a été présenté le 19 juin 2026 à VivaTech,
            sur le stand de Bpifrance, par la ministre de la Santé.
          </Description>
          <Description>
            Son objectif : développer une <strong>plateforme nationale souveraine en anatomopathologie
            et imagerie médicale</strong>, pour structurer et mutualiser les données, accélérer la recherche
            et faire émerger des outils d'intelligence artificielle souverains, durables et interopérables.
            Le projet fédère les CHU français, des centres de lutte contre le cancer et des laboratoires
            privés d'anatomie et cytologie pathologiques.
          </Description>
          <Description>
            Le projet s'articule autour de cinq briques : la structuration automatisée des comptes
            rendus médicaux, l'annotation semi-automatisée des images, un portail national de recherche
            multicentrique, une plateforme de mutualisation des données et une place de marché française
            d'algorithmes d'IA en pathologie.
          </Description>
          <Description>
            Porté par <strong>Enovacom</strong> (groupe Orange), il réunit un consortium de partenaires
            industriels, scientifiques et hospitaliers. Il est doté de 17 millions d'euros dans le cadre
            de France 2030.
          </Description>
          <Description>
            <HighlightedText>Thinkfab contribue à deux de ces briques.</HighlightedText> Sur{' '}
            <strong>la structuration des comptes rendus</strong>, c'est le terrain d'Impulsion, que nous
            développons en partenariat avec l'AFAQAP depuis plusieurs années : au sein du consortium,
            nous travaillons à la saisie des comptes rendus structurés, y compris à partir de la voix.
            Sur <strong>la mutualisation des données</strong>, nous travaillons en amont à la structuration
            des données et à leur modélisation selon le standard d'interopérabilité FHIR, car une donnée ne
            se partage bien que si elle est structurée dès sa production.
          </Description>
        </HeroSection>

        <ContentSection tinted>
          <SectionTitle>Impulsion.</SectionTitle>
          <ImpulsionIntro>
            Impulsion est la plateforme de comptes rendus structurés et standardisés en anatomie et
            cytologie pathologiques, développée en partenariat avec l'AFAQAP. Elle vise à simplifier le
            quotidien des pathologistes, à valoriser la spécialité par ses données et à préparer les
            services numériques de demain.
          </ImpulsionIntro>
          <PillarsGrid>
            {impulsionPillars.map((pillar) => (
              <Pillar key={pillar.name}>
                <PillarVerb>{pillar.verb}</PillarVerb>
                <PillarName>{pillar.name}</PillarName>
                <PillarText>{pillar.text}</PillarText>
              </Pillar>
            ))}
          </PillarsGrid>
          <ExternalLink href="https://impulsion-acp.fr" target="_blank" rel="noopener noreferrer">
            Découvrir impulsion-acp.fr
          </ExternalLink>
        </ContentSection>

        <ContentSection>
          <SectionTitle>Les partenaires.</SectionTitle>
          <PartnersGrid>
            {partners.map((partner) => (
              <PartnerCard key={partner.name}>
                {partner.isThinkfab ? (
                  <ThinkfabPartnerLogo />
                ) : partner.logo ? (
                  <PartnerLogo src={partner.logo} alt={`Logo ${partner.name}`} />
                ) : (
                  <PartnerName>{partner.name}</PartnerName>
                )}
                {partner.role && <PartnerRole>{partner.role}</PartnerRole>}
              </PartnerCard>
            ))}
          </PartnersGrid>
          <ExternalLink href={BPIFRANCE_ANNOUNCEMENT_URL} target="_blank" rel="noopener noreferrer">
            L'annonce des lauréats par Bpifrance
          </ExternalLink>
        </ContentSection>

        <FundingNotice>
          <FundingLogos>
            {fundingLogos.map((logo) => (
              <FundingLogo
                key={logo.src}
                src={logo.src}
                alt={logo.alt}
                onError={(event) => { event.currentTarget.style.display = 'none'; }}
              />
            ))}
          </FundingLogos>
          <FundingText>Ce projet a été financé par l'État dans le cadre de France 2030.</FundingText>
        </FundingNotice>
      </MainSection>

      <Footer
        pageContext="Page InnoPath"
        heading={
          <>
            Parlons de vos données de santé.
            <span role="img" aria-label="waving hand">👋</span>
          </>
        }
        subheading="Structuration, interopérabilité, comptes rendus : discutons de votre projet."
        buttonText="Envoyez un message"
        buttonLink="/contact"
      />
    </PageContainer>
  );
};

export default Innopath;
